import { dayjs, formatDate } from "@/core/utils/date";
import type {
  QuickbooksAccount,
  QuickbooksConnection,
  QuickbooksConnectionDetails,
} from "./quickbooks";

/** The API's date-only format for `bills_start_date`. */
export const QUICKBOOKS_DATE_FORMAT = "YYYY-MM-DD";

/** The values the bill setup screen edits, in the PATCH body's shape. */
export interface QuickbooksBillSettings {
  push_bills_enabled: boolean;
  bills_start_date: string | null;
  default_expense_account_id: string | null;
}

/** True while QuickBooks is connected but the account mirror has not synced yet. */
export const isQuickbooksSyncing = (connection?: QuickbooksConnection) =>
  !!connection &&
  connection.status !== "not_connected" &&
  connection.mirror_counts.accounts === 0;

/** How an account reads in pickers: nested accounts keep their parent path. */
export const quickbooksAccountLabel = (account: QuickbooksAccount) =>
  account.fully_qualified_name || account.name;

/**
 * What the setup screen starts from: the saved settings, else the API's
 * suggested account and today as the start date.
 *
 * @param today - `YYYY-MM-DD`, injected so the default is testable.
 */
export const initialQuickbooksBillSettings = (
  connection: QuickbooksConnectionDetails,
  today: string = dayjs().format(QUICKBOOKS_DATE_FORMAT),
): QuickbooksBillSettings => ({
  push_bills_enabled: connection.push_bills_enabled,
  bills_start_date: connection.bills_start_date ?? today,
  default_expense_account_id:
    (connection.default_expense_account ?? connection.suggested_expense_account)
      ?.id ?? null,
});

/** "Connected by Ana Ruiz on Oct 14, 2026", without a name when none is known. */
export const quickbooksConnectedLine = (
  connection: QuickbooksConnectionDetails,
) =>
  connection.connected_by
    ? `Connected by ${connection.connected_by.name} on ${formatDate(connection.connected_at)}`
    : `Connected on ${formatDate(connection.connected_at)}`;

/** One sentence on whether bills are being created, and from when, to where. */
export const quickbooksBillPushSummary = ({
  push_bills_enabled,
  bills_start_date,
  default_expense_account,
}: QuickbooksConnectionDetails) => {
  if (!push_bills_enabled || !bills_start_date || !default_expense_account) {
    return "Off. Volca isn’t creating bills in QuickBooks.";
  }
  if (!default_expense_account.active) {
    return `Paused. ${quickbooksAccountLabel(default_expense_account)} is no longer active in QuickBooks, so choose another default account.`;
  }

  return `Bills are created for invoices dated ${formatDate(bills_start_date)} or later, filed to ${quickbooksAccountLabel(default_expense_account)}.`;
};
